/**
 * Bericht anlegen/bearbeiten – eigenständige Tab-Seite
 */
import { useState, useEffect } from 'react';
import { FileText, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

export default function BerichtPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};
  const editData = params.editData;
  const currentYear = new Date().getFullYear();

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    typ: 'rechnungslegung', berichtsjahr: currentYear,
    zeitraum_von: '', zeitraum_bis: '', status: 'entwurf', bemerkungen: ''
  });

  useEffect(() => {
    if (editData) {
      setForm({
        typ: editData.typ || 'rechnungslegung',
        berichtsjahr: editData.berichtsjahr || currentYear,
        zeitraum_von: editData.zeitraum_von || '',
        zeitraum_bis: editData.zeitraum_bis || '',
        status: editData.status || 'entwurf',
        bemerkungen: editData.bemerkungen || '',
      });
    } else {
      const lastYear = currentYear - 1;
      setForm({
        typ: 'rechnungslegung', berichtsjahr: lastYear,
        zeitraum_von: `${lastYear}-01-01`, zeitraum_bis: `${lastYear}-12-31`,
        status: 'entwurf', bemerkungen: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!form.zeitraum_von || !form.zeitraum_bis) { toast.error('Bitte Zeitraum angeben'); return; }
    setSaving(true);
    try {
      const payload = { ...form, klient_id: params.klientId };
      if (editData) {
        await betreuungAPI.updateRechnungslegung(editData.id, payload);
        toast.success('Bericht aktualisiert');
      } else {
        await betreuungAPI.createRechnungslegung(payload);
        toast.success('Bericht angelegt');
      }
      emitDataRefresh('betreuungsakte');
      closeTab(activeTab.id);
    } catch { toast.error('Fehler beim Speichern'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="bericht-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-indigo-600" />{editData ? 'Bericht bearbeiten' : 'Neuer Bericht'}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>{params.klientName || ''}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Typ</Label>
              <Select value={form.typ} onValueChange={v => setForm(p => ({ ...p, typ: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rechnungslegung">Rechnungslegung</SelectItem>
                  <SelectItem value="jahresbericht">Jahresbericht</SelectItem>
                  <SelectItem value="schlussbericht">Schlussbericht</SelectItem>
                  <SelectItem value="anfangsbericht">Anfangsbericht</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Berichtsjahr</Label>
              <Input type="number" value={form.berichtsjahr} onChange={e => setForm(p => ({ ...p, berichtsjahr: parseInt(e.target.value) || currentYear }))} min={2000} max={2099} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Zeitraum von</Label><Input type="date" value={form.zeitraum_von} onChange={e => setForm(p => ({ ...p, zeitraum_von: e.target.value }))} /></div>
            <div><Label className="text-xs">Zeitraum bis</Label><Input type="date" value={form.zeitraum_bis} onChange={e => setForm(p => ({ ...p, zeitraum_bis: e.target.value }))} /></div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entwurf">Entwurf</SelectItem>
                <SelectItem value="eingereicht">Eingereicht</SelectItem>
                <SelectItem value="genehmigt">Genehmigt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bemerkungen</Label>
            <Textarea value={form.bemerkungen} onChange={e => setForm(p => ({ ...p, bemerkungen: e.target.value }))} rows={2} placeholder="Optionale Notizen..." />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-bericht">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Check className="w-4 h-4 mr-2" />{editData ? 'Speichern' : 'Anlegen'}</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
